import { CARD_PROVIDERS, currencySchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { RECONCILIATION_REVERSAL_KINDS, RECONCILIATION_STATUSES } from './reconciliation.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

const isoDaySchema = z.iso.date('a day is a real YYYY-MM-DD date');

const nonNegativeMinorSchema = z.string().regex(/^\d{1,18}$/, 'must be an integer in minor units');

export const PAYOUT_RECONCILIATION_STATUSES = ['confirmed', 'reversed'] as const;

/** The most captures one payout names — a card acquirer's busiest day, with room to spare. */
export const MAX_PAYOUT_CAPTURES = 1000;

/**
 * The widest window of captures one read looks at, in days. A provider pays out
 * daily or weekly, and the read probes every capture in the window, so a month
 * bounds it — docs/plans/05-finance-reconciliation.md §5.7.
 */
export const MAX_PAYOUT_WINDOW_DAYS = 31;

/** What the screen may leave out of a window by hand before it should narrow the window instead. */
export const MAX_PAYOUT_EXCLUDED_CAPTURES = 100;

/**
 * Why the captures a reader is looking at cannot be confirmed against the
 * chosen bank line as they stand. The write judges all of it again.
 */
/**
 * What a provider netted out of a payout instead of sending a debit — block E
 * of docs/plans/06-portal-and-field.md §6.2. The server does the arithmetic:
 * **gross − fee − deductions = the bank line**.
 */
export const PAYOUT_DEDUCTION_KINDS = ['refund', 'chargeback', 'dispute_fee'] as const;

export const payoutDeductionSchema = z.object({
  kind: z.enum(PAYOUT_DEDUCTION_KINDS),
  amount: moneySchema,
  providerReversalId: z.string().nullable(),
  captureTransactionId: z.uuid().nullable(),
});

export type PayoutDeductionView = z.infer<typeof payoutDeductionSchema>;

export const payoutDeductionInputSchema = z.object({
  kind: z.enum(PAYOUT_DEDUCTION_KINDS),
  amountMinor: z.string().regex(/^[1-9]\d{0,17}$/, 'must be a positive integer in minor units'),
  providerReversalId: z.string().min(1).max(200).nullable(),
  captureTransactionId: z.uuid().nullable(),
});

export type PayoutDeductionInput = z.infer<typeof payoutDeductionInputSchema>;

/** A payout names at most this many netted reversals; beyond it the window is too wide. */
export const MAX_PAYOUT_DEDUCTIONS = 200;

export const PAYOUT_PREVIEW_REFUSALS = [
  'PAYOUT_BANK_LINE_NOT_SETTLEMENT',
  'PAYOUT_BANK_LINE_RECONCILED',
  'PAYOUT_CAPTURE_NOT_ELIGIBLE',
  'PAYOUT_NO_CAPTURES',
  'PAYOUT_TOO_MANY_CAPTURES',
  'PAYOUT_AMOUNT_MISMATCH',
] as const;

/** A `card_settlement` credit a payout reconciliation has not taken yet — the screen's left side. */
export const settlementLineSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  organizationAccountId: z.uuid(),
  accountLabel: z.string(),
  bookedOn: isoDaySchema,
  amount: moneySchema,
  description: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  externalRef: z.string().nullable(),
});

export type SettlementLine = z.infer<typeof settlementLineSchema>;

export const settlementLinesInputSchema = paginationInputSchema.extend({
  organizationAccountId: z.uuid(),
});

export type SettlementLinesInput = z.infer<typeof settlementLinesInputSchema>;

/**
 * A verified capture no confirmed payout holds. Its match is shown because a
 * refunded or charged-back capture is usually netted out of the payout by the
 * provider, and a person decides whether to leave it out.
 */
export const payoutCaptureSchema = z.object({
  id: z.uuid(),
  bookedOn: isoDaySchema,
  amount: moneySchema,
  providerTransactionId: z.string(),
  maskedPan: z.string().nullable(),
  donationId: z.uuid().nullable(),
  referenceCode: z.string().nullable(),
  matchStatus: z.enum(RECONCILIATION_STATUSES).nullable(),
  reversalKind: z.enum(RECONCILIATION_REVERSAL_KINDS).nullable(),
  included: z.boolean(),
});

export type PayoutCapture = z.infer<typeof payoutCaptureSchema>;

export const payoutCapturesInputSchema = z.object({
  organizationAccountId: z.uuid(),
  from: isoDaySchema,
  to: isoDaySchema,
  bankTransactionId: z.uuid().optional(),
  excludedCaptureIds: z.array(z.uuid()).max(MAX_PAYOUT_EXCLUDED_CAPTURES).optional(),
  /**
   * What the provider netted out of this payout, so the fee the screen shows is
   * `gross − fee − deductions = the bank line` rather than the arithmetic block
   * E replaced. It is a **figure the screen was handed by
   * `providerReport`**, and the confirm judges it again under its own locks
   * against the deductions it is given — .claude/rules/money.md.
   */
  deductionsMinor: nonNegativeMinorSchema.optional(),
});

export type PayoutCapturesInput = z.infer<typeof payoutCapturesInputSchema>;

/**
 * The right side of the payout screen: every unpaid-out capture of one
 * provider account in a window, which of them the reader left out, and — given
 * a bank line — the fee as the included gross minus that line, decided on the
 * server so the screen adds nothing up.
 */
export const payoutCapturesSchema = z.object({
  provider: z.enum(CARD_PROVIDERS),
  items: z.array(payoutCaptureSchema),
  total: z.number().int(),
  truncated: z.boolean(),
  includedCount: z.number().int(),
  gross: moneySchema,
  bankAmount: moneySchema.nullable(),
  fee: moneySchema.nullable(),
  refusal: z.enum(PAYOUT_PREVIEW_REFUSALS).nullable(),
});

export type PayoutCaptures = z.infer<typeof payoutCapturesSchema>;

export const payoutReconciliationSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  provider: z.enum(CARD_PROVIDERS),
  status: z.enum(PAYOUT_RECONCILIATION_STATUSES),
  bankTransaction: z.object({
    id: z.uuid(),
    organizationAccountId: z.uuid(),
    accountLabel: z.string(),
    bookedOn: isoDaySchema,
    description: z.string().nullable(),
    externalRef: z.string().nullable(),
    amount: moneySchema,
  }),
  captureAccount: z.object({ id: z.uuid(), label: z.string() }),
  captureCount: z.number().int(),
  gross: moneySchema,
  fee: moneySchema,
  deductionsTotal: moneySchema,
  deductions: z.array(payoutDeductionSchema),
  confirmedByName: z.string().nullable(),
  confirmedAt: z.string(),
  reversedByName: z.string().nullable(),
  reversedAt: z.string().nullable(),
  reversalReason: z.string().nullable(),
});

export type PayoutReconciliation = z.infer<typeof payoutReconciliationSchema>;

export const payoutReconciliationListInputSchema = paginationInputSchema.extend({
  organizationAccountId: z.uuid().optional(),
  status: z.enum(PAYOUT_RECONCILIATION_STATUSES).optional(),
});

export type PayoutReconciliationListInput = z.infer<typeof payoutReconciliationListInputSchema>;

export const confirmPayoutInputSchema = z.object({
  bankTransactionId: z.uuid(),
  captureTransactionIds: z
    .array(z.uuid())
    .min(1)
    .max(MAX_PAYOUT_CAPTURES)
    .refine((ids) => new Set(ids).size === ids.length, 'a capture appears once in one payout'),
  feeMinor: nonNegativeMinorSchema,
  deductions: z.array(payoutDeductionInputSchema).max(MAX_PAYOUT_DEDUCTIONS).optional(),
});

export type ConfirmPayoutInput = z.infer<typeof confirmPayoutInputSchema>;

/**
 * Card payouts reconciled at account level —
 * docs/plans/05-finance-reconciliation.md §5.7. **Nothing here moves a
 * donation**: each capture settled its donation when it was verified.
 */
/**
 * What the provider says a payout was made of, read with that organization's own
 * credential so the screen prefills what it would otherwise ask an operator to
 * type — docs/plans/06-portal-and-field.md §6.2 block E.
 *
 * It is a **read**: nothing is written, nothing is confirmed, and the operator
 * still presses confirm. The figures come back in the same shapes `confirm`
 * takes them, so the screen hands them straight back rather than transforming
 * anything on the way.
 */
export const providerPayoutReportSchema = z.object({
  provider: z.enum(CARD_PROVIDERS),
  providerPayoutId: z.string(),
  currency: currencySchema,
  gross: moneySchema,
  fee: moneySchema,
  captureCount: z.number().int().min(0),
  deductions: z.array(payoutDeductionInputSchema),
});

export type ProviderPayoutReport = z.infer<typeof providerPayoutReportSchema>;

export const providerPayoutReportInputSchema = z.object({
  organizationAccountId: z.uuid(),
  providerPayoutId: z.string().trim().min(1).max(200),
});

export type ProviderPayoutReportInput = z.infer<typeof providerPayoutReportInputSchema>;

export const payoutReconciliationContract = {
  settlementLines: oc
    .route({ method: 'GET', path: '/payout-reconciliations/settlement-lines' })
    .errors(guarded)
    .input(settlementLinesInputSchema)
    .output(paginated(settlementLineSchema)),

  captures: oc
    .route({ method: 'GET', path: '/payout-reconciliations/captures' })
    .errors(guarded)
    .input(payoutCapturesInputSchema)
    .output(payoutCapturesSchema),

  list: oc
    .route({ method: 'GET', path: '/payout-reconciliations' })
    .errors(guarded)
    .input(payoutReconciliationListInputSchema)
    .output(paginated(payoutReconciliationSchema)),

  /**
   * The bank line is allocated whole and the fee written to the ledger. The
   * reader sends the fee it was shown, so a capture set that changed underneath
   * the screen is refused rather than confirmed at another fee.
   */
  confirm: oc
    .route({ method: 'POST', path: '/payout-reconciliations' })
    .errors(guarded)
    .input(confirmPayoutInputSchema)
    .output(z.object({ payout: payoutReconciliationSchema })),

  /** Frees the bank line and the captures, and gives the fee back to the ledger. */
  /**
   * The provider's own report for one payout, so the deductions are read rather
   * than typed. Behind `reconciliation.match` rather than `reconciliation.list`:
   * it spends that organization's secret key on an outbound call and answers
   * with the figures a confirmation is made of, so it belongs to whoever may
   * confirm — .claude/rules/auth-security.md.
   */
  providerReport: oc
    .route({ method: 'GET', path: '/payout-reconciliations/provider-report' })
    .errors(guarded)
    .input(providerPayoutReportInputSchema)
    .output(providerPayoutReportSchema),

  reverse: oc
    .route({ method: 'POST', path: '/payout-reconciliations/{id}/reverse' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), reason: z.string().trim().min(1).max(500) }))
    .output(z.object({ payout: payoutReconciliationSchema })),
};
